import React, { useState, useEffect } from 'react';
import { MdVisibility, MdRestore } from 'react-icons/md';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import DateFnsUtils from '@date-io/date-fns';
import { format } from 'date-fns';

import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tab,
    Tabs,
    DialogContentText,
} from '@material-ui/core';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';

import DataTable from '~/components/DataTable';
import PageContent from '~/components/PageContent';
import SimpleDialog from '~/components/SimpleDialog';

import api from '~/services/pluginbot-api';
import getDateLocale from '~/util/GetDateLocale';

import { getCategoriesOptions, getModelLink } from './categoryMappings';

function SystemChanges({ intl }) {
    const _settings = useSelector(state => state.settings || null);
    const date_loc = getDateLocale(_settings);

    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('human');
    const [logs, setLogs] = useState([]);
    const [filter, setFilter] = useState('all');
    const [filterOptions, setFilterOptions] = useState([
        {
            labelKey: 'system_logs.category.general',
            options: [{ value: 'all', labelKey: 'system_logs.filter.all' }],
        },
    ]);
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [revertDialogOpen, setRevertDialogOpen] = useState(false);
    const [logToRevert, setLogToRevert] = useState(null);

    const handleCategoryChange = e => {
        const idx = Number(e.target.value);
        setSelectedCategoryIndex(idx);
        const group = filterOptions[idx];
        if (group && group.options && group.options.length > 0) {
            setFilter(group.options[0].value);
        }
    };

    function handleRevertClick(logId) {
        setLogToRevert(logId);
        setRevertDialogOpen(true);
    }

    async function executeRevert() {
        setRevertDialogOpen(false);
        setIsLoading(true);
        try {
            await api.post(`/admin/system-changes/${logToRevert}/revert`);
            toast.success(
                intl.formatMessage({
                    id: 'system_logs.feedback.recover_success',
                    defaultMessage: 'Item recuperado com sucesso!',
                })
            );
            // eslint-disable-next-line no-use-before-define
            await loadLogs();
        } catch (error) {
            toast.error(
                (error.response &&
                    error.response.data &&
                    error.response.data.error) ||
                    intl.formatMessage({
                        id: 'system_logs.feedback.recover_error',
                        defaultMessage: 'Erro ao recuperar o item.',
                    })
            );
        } finally {
            setIsLoading(false);
        }
    }

    const formatLogDesc = log => {
        if (log.descriptionKey) {
            return intl.formatMessage(
                { id: log.descriptionKey, defaultMessage: log.descriptionKey },
                log.descriptionParams || {}
            );
        }
        return (
            log.description ||
            intl.formatMessage({
                id: 'system_logs.unknown',
                defaultMessage: 'Desconhecido',
            })
        );
    };

    const formatLogType = type => {
        if (!type) return '';
        return intl.formatMessage({
            id: `model.name.${type}`,
            defaultMessage: type,
        });
    };

    const formatLogAction = action => {
        const keyMap = {
            created: 'system_logs.action.created',
            deleted: 'system_logs.action.deleted',
            updated: 'system_logs.action.updated',
            system: 'system_logs.action.system',
        };
        const id = keyMap[action];
        return id ? intl.formatMessage({ id, defaultMessage: action }) : action;
    };

    async function loadLogs() {
        setIsLoading(true);
        try {
            let endpoint = '/admin/system-changes';
            let finalFilter = filter;
            if (activeTab === 'machine') endpoint = '/admin/machine-logs';
            if (activeTab === 'sessions')
                endpoint = '/admin/system-changes/sessions';
            if (activeTab === 'deleted') {
                endpoint = '/admin/system-changes';
                finalFilter = 'deleted_logs';
            }

            const response = await api.get(endpoint, {
                params: {
                    module_filter: finalFilter,
                    start_date: startDate
                        ? format(startDate, 'yyyy-MM-dd')
                        : undefined,
                    end_date: endDate
                        ? format(endDate, 'yyyy-MM-dd')
                        : undefined,
                },
            });

            if (response.data && response.data.logs) {
                setFilterOptions(getCategoriesOptions(activeTab));
                setLogs(
                    response.data.logs.map(log => {
                        if (activeTab === 'sessions') {
                            return {
                                ...log,
                                id: `session_${log.id}_${Math.random()}`,
                                user:
                                    log.user ||
                                    intl.formatMessage({
                                        id: 'system_logs.unknown',
                                        defaultMessage: 'Desconhecido',
                                    }),
                                timestamp: new Date(log.updatedAt).getTime(),
                                formattedDate: format(
                                    new Date(log.updatedAt),
                                    'dd/MM/yyyy HH:mm',
                                    { locale: date_loc }
                                ),
                            };
                        }
                        return {
                            ...log,
                            type: formatLogType(log.type),
                            description: formatLogDesc(log),
                            id: `log_${log.type}_${log.id}_${Math.random()}`,
                            timestamp: new Date(log.updatedAt).getTime(),
                            formattedDate: format(
                                new Date(log.updatedAt),
                                'dd/MM/yyyy HH:mm',
                                { locale: date_loc }
                            ),
                            actionElement: (
                                <span
                                    className={`badge badge-${{
                                        created: 'success',
                                        deleted: 'danger',
                                        updated: 'primary',
                                        system: 'info',
                                    }[log.action] || 'primary'}`}
                                >
                                    {formatLogAction(log.action)}
                                </span>
                            ),
                            linkElement: (
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {log.action !== 'deleted' && (
                                        <Link
                                            to={
                                                log.link ||
                                                getModelLink(log.type)
                                            }
                                            title={intl.formatMessage({
                                                id:
                                                    'system_logs.action.view_details',
                                                defaultMessage: 'Ver Detalhes',
                                            })}
                                            style={{
                                                color: '#007bff',
                                                fontSize: '20px',
                                            }}
                                        >
                                            <MdVisibility />
                                        </Link>
                                    )}
                                    {log.action === 'deleted' && (
                                        <button
                                            type="button"
                                            title={intl.formatMessage({
                                                id:
                                                    'system_logs.action.recover_item',
                                                defaultMessage:
                                                    'Recuperar Item',
                                            })}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                color: '#ffc107',
                                                fontSize: '20px',
                                                cursor: 'pointer',
                                                padding: 0,
                                            }}
                                            onClick={() =>
                                                handleRevertClick(log.id)
                                            }
                                        >
                                            <MdRestore />
                                        </button>
                                    )}
                                </div>
                            ),
                        };
                    })
                );
            } else {
                setFilterOptions(getCategoriesOptions(activeTab));
                setLogs(
                    response.data.map(log => {
                        if (activeTab === 'sessions') {
                            return {
                                ...log,
                                id: `session_${log.id}_${Math.random()}`,
                                user:
                                    log.user ||
                                    intl.formatMessage({
                                        id: 'system_logs.unknown',
                                        defaultMessage: 'Desconhecido',
                                    }),
                                timestamp: new Date(log.updatedAt).getTime(),
                                formattedDate: format(
                                    new Date(log.updatedAt),
                                    'dd/MM/yyyy HH:mm',
                                    { locale: date_loc }
                                ),
                            };
                        }
                        return {
                            ...log,
                            type: formatLogType(log.type),
                            description: formatLogDesc(log),
                            id: `log_${log.type}_${log.id}_${Math.random()}`,
                            timestamp: new Date(log.updatedAt).getTime(),
                            formattedDate: format(
                                new Date(log.updatedAt),
                                'dd/MM/yyyy HH:mm',
                                { locale: date_loc }
                            ),
                            actionElement: (
                                <span
                                    className={`badge badge-${{
                                        created: 'success',
                                        deleted: 'danger',
                                        updated: 'primary',
                                        system: 'info',
                                    }[log.action] || 'primary'}`}
                                >
                                    {formatLogAction(log.action)}
                                </span>
                            ),
                            linkElement: (
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '10px',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {log.action !== 'deleted' && (
                                        <Link
                                            to={
                                                log.link ||
                                                getModelLink(log.type)
                                            }
                                            title={intl.formatMessage({
                                                id:
                                                    'system_logs.action.view_details',
                                                defaultMessage: 'Ver Detalhes',
                                            })}
                                            style={{
                                                color: '#007bff',
                                                fontSize: '20px',
                                            }}
                                        >
                                            <MdVisibility />
                                        </Link>
                                    )}
                                    {log.action === 'deleted' && (
                                        <button
                                            type="button"
                                            title={intl.formatMessage({
                                                id:
                                                    'system_logs.action.recover_item',
                                                defaultMessage:
                                                    'Recuperar Item',
                                            })}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                color: '#ffc107',
                                                fontSize: '20px',
                                                cursor: 'pointer',
                                                padding: 0,
                                            }}
                                            onClick={() =>
                                                handleRevertClick(log.id)
                                            }
                                        >
                                            <MdRestore />
                                        </button>
                                    )}
                                </div>
                            ),
                        };
                    })
                );
            }
        } catch (error) {
            toast.error(
                intl.formatMessage({
                    id: 'system_logs.feedback.fetch_error',
                    defaultMessage:
                        'Erro ao buscar as modificações recentes do sistema.',
                })
            );
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, startDate, endDate, activeTab]);

    let columns = [
        {
            label: intl.formatMessage({ id: 'table.column.date' }),
            id: 'formattedDate',
            order_by: 'timestamp',
            width: '150px',
        },
        {
            label: intl.formatMessage({ id: 'table.column.user' }),
            id: 'user',
            width: '200px',
        },
        {
            label: intl.formatMessage({ id: 'table.column.module' }),
            id: 'type',
            width: '150px',
        },
        {
            label: intl.formatMessage({ id: 'table.column.description' }),
            id: 'description',
        },
        {
            label: intl.formatMessage({ id: 'table.column.action' }),
            id: 'actionElement',
            width: '100px',
        },
        {
            label: intl.formatMessage({ id: 'table.column.link' }),
            id: 'linkElement',
            width: '100px',
            align: 'center',
        },
    ];

    if (activeTab === 'machine') {
        columns = columns.filter(col => col.id !== 'user');
    } else if (activeTab === 'sessions') {
        columns = [
            {
                label: intl.formatMessage({ id: 'table.column.date' }),
                id: 'formattedDate',
                order_by: 'timestamp',
                width: '150px',
            },
            {
                label: intl.formatMessage({ id: 'table.column.user' }),
                id: 'user',
                width: '250px',
            },
            { label: 'IP', id: 'ip', width: '150px' },
            {
                label: intl.formatMessage({ id: 'table.column.device' }),
                id: 'device',
            },
        ];
    } else if (activeTab === 'deleted') {
        // Keeps user and description, can customize if needed
    }

    return (
        <PageContent
            title={intl.formatMessage({ id: 'system_logs.title' })}
            currentPage={`Master / ${intl.formatMessage({
                id: 'system_logs.title',
            })}`}
            loading={isLoading}
        >
            <>
                <SimpleDialog
                    open={revertDialogOpen}
                    onClose={() => setRevertDialogOpen(false)}
                    title={intl.formatMessage({
                        id: 'system_logs.action.recover_item',
                        defaultMessage: 'Recuperar Item',
                    })}
                    content={
                        <DialogContentText>
                            {intl.formatMessage({
                                id: 'system_logs.dialog.recover_confirm',
                                defaultMessage:
                                    'Tem certeza que deseja recuperar este item?',
                            })}
                        </DialogContentText>
                    }
                    actions={[
                        {
                            key: 'cancel',
                            onClick: () => setRevertDialogOpen(false),
                            label: intl.formatMessage({
                                id: 'button.cancel',
                                defaultMessage: 'Cancelar',
                            }),
                        },
                        {
                            key: 'revert',
                            onClick: executeRevert,
                            label: intl.formatMessage({
                                id: 'button.recover',
                                defaultMessage: 'Recuperar',
                            }),
                        },
                    ]}
                />
                <div className="col-12 mb-3">
                    <Tabs
                        indicatorColor="primary"
                        value={activeTab}
                        onChange={(event, newValue) => {
                            setActiveTab(newValue);
                            setFilter('all');
                            setSelectedCategoryIndex(0);
                        }}
                        style={{ marginBottom: '20px' }}
                    >
                        <Tab
                            label={intl.formatMessage({
                                id: 'system_logs.tabs.human',
                                defaultMessage: 'Modificações Manuais',
                            })}
                            value="human"
                        />
                        <Tab
                            label={intl.formatMessage({
                                id: 'system_logs.tabs.machine',
                                defaultMessage: 'Logs de Robôs e Sistema',
                            })}
                            value="machine"
                        />
                        <Tab
                            label={intl.formatMessage({
                                id: 'system_logs.tabs.sessions',
                                defaultMessage: 'Sessões e Acessos',
                            })}
                            value="sessions"
                        />
                        <Tab
                            label={intl.formatMessage({
                                id: 'system_logs.tabs.deleted',
                                defaultMessage: 'Registros Excluídos',
                            })}
                            value="deleted"
                        />
                    </Tabs>
                    <div>
                        <div>
                            <DataTable
                                headerColumns={columns}
                                data={logs}
                                hasFilter
                                hasHeader
                                header={
                                    <div
                                        className="d-flex align-items-end"
                                        style={{ gap: '15px' }}
                                    >
                                        {activeTab !== 'sessions' &&
                                            activeTab !== 'deleted' && (
                                                <>
                                                    <FormControl
                                                        variant="outlined"
                                                        size="small"
                                                        style={{
                                                            width: '220px',
                                                        }}
                                                    >
                                                        <InputLabel id="category_filter_label">
                                                            {intl.formatMessage(
                                                                {
                                                                    id:
                                                                        'system_logs.filter.category',
                                                                    defaultMessage:
                                                                        'Categoria',
                                                                }
                                                            )}
                                                        </InputLabel>
                                                        <Select
                                                            labelId="category_filter_label"
                                                            id="category_filter"
                                                            value={
                                                                selectedCategoryIndex
                                                            }
                                                            onChange={
                                                                handleCategoryChange
                                                            }
                                                            label={intl.formatMessage(
                                                                {
                                                                    id:
                                                                        'system_logs.filter.category',
                                                                    defaultMessage:
                                                                        'Categoria',
                                                                }
                                                            )}
                                                        >
                                                            {filterOptions.map(
                                                                (
                                                                    group,
                                                                    idx
                                                                ) => (
                                                                    <MenuItem
                                                                        key={
                                                                            idx
                                                                        }
                                                                        value={
                                                                            idx
                                                                        }
                                                                    >
                                                                        {group.labelKey
                                                                            ? intl.formatMessage(
                                                                                  {
                                                                                      id:
                                                                                          group.labelKey,
                                                                                  }
                                                                              )
                                                                            : group.label}
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                    <FormControl
                                                        variant="outlined"
                                                        size="small"
                                                        style={{
                                                            width: '220px',
                                                        }}
                                                    >
                                                        <InputLabel id="module_filter_label">
                                                            {intl.formatMessage(
                                                                {
                                                                    id:
                                                                        'system_logs.filter.subitem',
                                                                    defaultMessage:
                                                                        'Subitem',
                                                                }
                                                            )}
                                                        </InputLabel>
                                                        <Select
                                                            labelId="module_filter_label"
                                                            id="module_filter"
                                                            value={filter}
                                                            onChange={e =>
                                                                setFilter(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            label={intl.formatMessage(
                                                                {
                                                                    id:
                                                                        'system_logs.filter.subitem',
                                                                    defaultMessage:
                                                                        'Subitem',
                                                                }
                                                            )}
                                                        >
                                                            {filterOptions[
                                                                selectedCategoryIndex
                                                            ] &&
                                                                filterOptions[
                                                                    selectedCategoryIndex
                                                                ].options &&
                                                                filterOptions[
                                                                    selectedCategoryIndex
                                                                ].options.map(
                                                                    opt => (
                                                                        <MenuItem
                                                                            key={
                                                                                opt.value
                                                                            }
                                                                            value={
                                                                                opt.value
                                                                            }
                                                                        >
                                                                            {opt.labelKey
                                                                                ? intl.formatMessage(
                                                                                      {
                                                                                          id:
                                                                                              opt.labelKey,
                                                                                      },
                                                                                      opt.labelParams ||
                                                                                          {}
                                                                                  )
                                                                                : opt.label}
                                                                        </MenuItem>
                                                                    )
                                                                )}
                                                        </Select>
                                                    </FormControl>
                                                </>
                                            )}
                                        <div style={{ width: '180px' }}>
                                            <MuiPickersUtilsProvider
                                                utils={DateFnsUtils}
                                                locale={date_loc}
                                            >
                                                <DatePicker
                                                    variant="inline"
                                                    inputVariant="outlined"
                                                    size="small"
                                                    label={intl.formatMessage({
                                                        id:
                                                            'system_logs.filter.start_date',
                                                        defaultMessage:
                                                            'Data Inicial',
                                                    })}
                                                    format="dd/MM/yyyy"
                                                    value={startDate}
                                                    onChange={date =>
                                                        setStartDate(date)
                                                    }
                                                    autoOk
                                                    ampm={false}
                                                    fullWidth
                                                />
                                            </MuiPickersUtilsProvider>
                                        </div>
                                        <div style={{ width: '180px' }}>
                                            <MuiPickersUtilsProvider
                                                utils={DateFnsUtils}
                                                locale={date_loc}
                                            >
                                                <DatePicker
                                                    variant="inline"
                                                    inputVariant="outlined"
                                                    size="small"
                                                    label={intl.formatMessage({
                                                        id:
                                                            'system_logs.filter.end_date',
                                                        defaultMessage:
                                                            'Data Final',
                                                    })}
                                                    format="dd/MM/yyyy"
                                                    value={endDate}
                                                    onChange={date =>
                                                        setEndDate(date)
                                                    }
                                                    autoOk
                                                    ampm={false}
                                                    fullWidth
                                                />
                                            </MuiPickersUtilsProvider>
                                        </div>
                                    </div>
                                }
                                hasActions={false}
                            />
                        </div>
                    </div>
                </div>
            </>
        </PageContent>
    );
}

export default injectIntl(SystemChanges);
